import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm, ValidationError } from '@formspree/react';

const features = [
  {
    title: 'Personalized to Your Lenses',
    description: 'Meal plans automatically exclude ingredients flagged by your active lenses — seed oils, dyes, whatever you avoid.',
  },
  {
    title: 'Family-Friendly Options',
    description: 'Create plans for the whole household with different dietary needs. Kids can have their preferences too.',
  },
  {
    title: 'Smart Shopping Lists',
    description: 'Generate grocery lists from your meal plan with ingredient alternatives when your usual picks aren\'t available.',
  },
];

function MealPlannerPage() {
  const [state, handleSubmit] = useForm('waitlist'); // Replace with your Formspree form ID
  const [email, setEmail] = useState('');

  return (
    <>
      <Helmet>
        <title>AI Meal Planner - Coming Soon | FoodLens AI</title>
        <meta name="description" content="Join the waitlist for FoodLens AI Meal Planner. Get personalized meal plans that automatically avoid your flagged ingredients." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-hero noise-overlay min-h-[80vh] flex items-center">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-warm-500/20 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-sage-200 rounded-full blur-3xl opacity-40" />

        <div className="section relative z-10 py-20">
          <div className="max-w-3xl mx-auto text-center">
            {/* Coming Soon Badge */}
            <div className="inline-flex items-center gap-2 bg-warm-500/10 text-warm-600 px-4 py-2 rounded-full text-sm font-medium mb-8">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Coming Soon
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-tight mb-6">
              AI Meal Planning —{' '}
              <span className="text-warm-500">Built Around Your Diet</span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-500 leading-relaxed mb-10 max-w-2xl mx-auto">
              Get personalized weekly meal plans that automatically exclude the ingredients you avoid.
              No more guessing what to cook — just clean, simple meals tailored to your family.
            </p>

            {/* Waitlist Form */}
            <div className="max-w-md mx-auto">
              {state.succeeded ? (
                <div className="bg-sage-100 border border-sage-200 rounded-2xl p-6 text-center">
                  <div className="w-12 h-12 bg-sage-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-display text-xl text-charcoal mb-2">You're on the list!</h3>
                  <p className="text-gray-500">We'll email you as soon as AI Meal Planner is ready.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="flex-1 px-5 py-4 rounded-xl border border-sage-200 bg-surface text-charcoal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-warm-500 focus:border-transparent"
                  />
                  <ValidationError prefix="Email" field="email" errors={state.errors} className="text-coral-500 text-sm" />
                  <button
                    type="submit"
                    disabled={state.submitting}
                    className="px-8 py-4 bg-warm-500 hover:bg-warm-600 text-white font-medium rounded-xl transition-all shadow-soft hover:shadow-medium disabled:opacity-50"
                  >
                    {state.submitting ? 'Joining...' : 'Join Waitlist'}
                  </button>
                </form>
              )}
              <p className="text-sm text-gray-500 mt-4">
                No spam, ever. We'll only email you about Meal Planner updates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Preview */}
      <section className="bg-surface py-20 md:py-28">
        <div className="section">
          <div className="text-center mb-16">
            <h2 className="section-heading">What to Expect</h2>
            <p className="section-subheading mx-auto">
              A sneak peek at what we're building.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="inline-flex w-14 h-14 rounded-2xl bg-warm-500/10 text-warm-600 items-center justify-center mb-4">
                  <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <h3 className="font-display text-xl text-charcoal mb-2">{feature.title}</h3>
                <p className="text-gray-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Simple FAQ */}
      <section className="bg-background py-20 md:py-28">
        <div className="section max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="section-heading">Questions?</h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: 'When will Meal Planner launch?',
                a: 'We\'re targeting early 2025. Join the waitlist to be the first to know.',
              },
              {
                q: 'Will it cost extra?',
                a: 'We haven\'t finalized pricing yet, but waitlist members will get early access and likely a discount.',
              },
              {
                q: 'Do I need the app to use it?',
                a: 'Meal Planner will work standalone, but it\'ll be even better when connected to your FoodLens app for seamless lens syncing.',
              },
            ].map((item) => (
              <div key={item.q} className="bg-surface rounded-2xl p-6 border border-sage-100">
                <h3 className="font-semibold text-charcoal mb-2">{item.q}</h3>
                <p className="text-gray-500">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-sage text-white py-20">
        <div className="section text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">
            Be the First to Try It
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-xl mx-auto">
            Join hundreds of others waiting for a smarter way to plan meals.
          </p>
          <a
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-sage-600 font-medium text-lg rounded-xl transition-all hover:bg-sage-50"
          >
            Join the Waitlist
            <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </a>
        </div>
      </section>
    </>
  );
}

export default MealPlannerPage;
